import type { CableTrayResponse, CableTrayWarning } from "../services/cableTray";
import { StatusIcon } from "./StatusIcon";
import { GROUP_TONE } from "./statusTone";

type WarningCode = CableTrayWarning["code"];

// The group values are the shared ones of statusTone.GROUP_TONE, so study.css holds a rule
// and a tone for each: the computed failures under "not-met", the evidence the engineer owes
// under "review-evidence", the linked runs' consistency codes under "fault-level", the stated
// items under "not-evaluated".
type WarningGroup = "not-met" | "review-evidence" | "fault-level" | "not-evaluated";

interface CableTrayWarningPanelProps {
  result: CableTrayResponse;
}

const NO_WARNINGS_TEXT = "No engineering warnings raised.";

// Exhaustive maps: a new code without a label or a group is a compile error.
const WARNING_CODE_LABELS: Record<WarningCode, string> = {
  FILL_LIMIT_EXCEEDED: "Fill above the declared limit",
  SINGLE_LAYER_WIDTH_EXCEEDED: "Usable width exceeded",
  COVER_DEPTH_EXCEEDED: "Usable depth exceeded under the cover",
  LOAD_EXCEEDED: "Load above the rated load",
  RATED_LOAD_SPAN_NOT_APPLICABLE: "Rated load not applicable at the support spacing",
  FILL_LIMIT_NOT_ESTABLISHED: "Fill limit not established",
  RATED_LOAD_NOT_DECLARED: "Rated load not declared",
  RATED_SPAN_NOT_DECLARED: "Rated span not declared",
  SUPPORT_SPACING_NOT_DECLARED: "Support spacing not declared",
  DIMENSIONS_SOURCE_NOT_DECLARED: "Dimensions source not declared",
  FILL_LIMIT_SOURCE_NOT_DECLARED: "Fill limit source not declared",
  RATED_LOAD_SOURCE_NOT_DECLARED: "Rated load source not declared",
  SUPPORT_SPACING_SOURCE_NOT_DECLARED: "Support spacing source not declared",
  OVERALL_DIAMETER_NOT_DECLARED: "Overall diameter not declared",
  MASS_NOT_DECLARED: "Mass per metre not declared",
  DIAMETER_SOURCE_NOT_DECLARED: "Diameter source not declared",
  MASS_SOURCE_NOT_DECLARED: "Mass source not declared",
  CABLE_RUN_NOT_VALID: "Linked cable run not a passed design check",
  INSTALLATION_METHOD_MISMATCH: "Installation method differs from the tray arrangement",
  GROUPING_DIFFERS: "Grouping differs from the route",
  INSTALLATION_METHOD_TRAY_GENERIC: "Generic tray method (arrangement not compared)",
  INSTALLATION_METHOD_ENGINEERED: "Engineered installation basis (not compared)",
  SPACING_NOT_DECLARED: "Spacing not declared (touching)",
  QUANTITY_FROM_LINKED_RUN: "Quantity from the linked cable run",
  FUTURE_ALLOWANCE_NOT_DECLARED: "Future allowance not declared",
  ALLOWANCE_NOT_APPLIED_TO_WIDTH: "Allowance not applied to the single-layer width",
  CIRCUMSCRIBED_CIRCLE_BASIS: "Circumscribed-circle area basis",
  SEGREGATION_NOT_CHECKED: "Segregation not checked",
  COVER_THERMAL_EFFECT_NOT_CHECKED: "Cover thermal effect not checked",
  EARTH_CONTINUITY_NOT_CHECKED: "Earth continuity not checked",
  BEND_RADIUS_NOT_CHECKED: "Bend radii not checked",
  TRAY_MECHANICAL_DESIGN_NOT_CHECKED: "Tray mechanical design not checked",
};

// Where each code is listed: the failed conditions first, then the evidence the engineer owes,
// then the linked runs' consistency, then what the sheet states (the F-1 lesson of IR-005: the
// not-checked items are always listed).
const WARNING_CODE_GROUPS: Record<WarningCode, WarningGroup> = {
  FILL_LIMIT_EXCEEDED: "not-met",
  SINGLE_LAYER_WIDTH_EXCEEDED: "not-met",
  COVER_DEPTH_EXCEEDED: "not-met",
  LOAD_EXCEEDED: "not-met",
  RATED_LOAD_SPAN_NOT_APPLICABLE: "review-evidence",
  FILL_LIMIT_NOT_ESTABLISHED: "review-evidence",
  RATED_LOAD_NOT_DECLARED: "review-evidence",
  RATED_SPAN_NOT_DECLARED: "review-evidence",
  SUPPORT_SPACING_NOT_DECLARED: "review-evidence",
  DIMENSIONS_SOURCE_NOT_DECLARED: "review-evidence",
  FILL_LIMIT_SOURCE_NOT_DECLARED: "review-evidence",
  RATED_LOAD_SOURCE_NOT_DECLARED: "review-evidence",
  SUPPORT_SPACING_SOURCE_NOT_DECLARED: "review-evidence",
  OVERALL_DIAMETER_NOT_DECLARED: "review-evidence",
  MASS_NOT_DECLARED: "review-evidence",
  DIAMETER_SOURCE_NOT_DECLARED: "review-evidence",
  MASS_SOURCE_NOT_DECLARED: "review-evidence",
  CABLE_RUN_NOT_VALID: "fault-level",
  INSTALLATION_METHOD_MISMATCH: "fault-level",
  GROUPING_DIFFERS: "fault-level",
  INSTALLATION_METHOD_TRAY_GENERIC: "fault-level",
  INSTALLATION_METHOD_ENGINEERED: "fault-level",
  SPACING_NOT_DECLARED: "not-evaluated",
  QUANTITY_FROM_LINKED_RUN: "not-evaluated",
  FUTURE_ALLOWANCE_NOT_DECLARED: "not-evaluated",
  ALLOWANCE_NOT_APPLIED_TO_WIDTH: "not-evaluated",
  CIRCUMSCRIBED_CIRCLE_BASIS: "not-evaluated",
  SEGREGATION_NOT_CHECKED: "not-evaluated",
  COVER_THERMAL_EFFECT_NOT_CHECKED: "not-evaluated",
  EARTH_CONTINUITY_NOT_CHECKED: "not-evaluated",
  BEND_RADIUS_NOT_CHECKED: "not-evaluated",
  TRAY_MECHANICAL_DESIGN_NOT_CHECKED: "not-evaluated",
};

const GROUP_ORDER: readonly { group: WarningGroup; title: string }[] = [
  { group: "not-met", title: "Fill or load not met" },
  { group: "review-evidence", title: "Review required (evidence)" },
  { group: "fault-level", title: "Linked cable runs (information)" },
  { group: "not-evaluated", title: "Stated on the sheet (not checked here)" },
];

type ListedWarning = {
  key: string;
  code: WarningCode;
  message: string;
  prefix: string;
};

/** The route's warnings followed by every row's, in the engine's order, rows named. */
export function listWarnings(result: CableTrayResponse): ListedWarning[] {
  const route = result.warnings.map((warning, index) => ({
    key: `route-${warning.code}-${index}`,
    code: warning.code,
    message: warning.message,
    prefix: "",
  }));
  const rows = result.rows.flatMap((row) =>
    row.warnings.map((warning, index) => ({
      key: `${row.code}-${warning.code}-${index}`,
      code: warning.code,
      message: warning.message,
      prefix: `Row ${row.code}: `,
    })),
  );
  return [...route, ...rows];
}

export function CableTrayWarningPanel({ result }: CableTrayWarningPanelProps) {
  const warnings = listWarnings(result);

  return (
    <section aria-label="Engineering warnings">
      <h3>Engineering warnings</h3>
      {warnings.length === 0 ? (
        <p data-no-warnings="true">{NO_WARNINGS_TEXT}</p>
      ) : (
        <div data-warning-count={warnings.length}>
          {GROUP_ORDER.map(({ group, title }) => {
            const members = warnings.filter((warning) => WARNING_CODE_GROUPS[warning.code] === group);
            if (members.length === 0) {
              return null;
            }
            return (
              <section key={group} aria-label={title} data-warning-group={group}>
                <h4>
                  <StatusIcon tone={GROUP_TONE[group]} size={20} />
                  {title}
                </h4>
                <ul>
                  {members.map((warning) => (
                    <li key={warning.key} data-warning-code={warning.code}>
                      <strong>
                        {warning.prefix}
                        {WARNING_CODE_LABELS[warning.code]}
                      </strong>
                      {": "}
                      <span>{warning.message}</span>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </section>
  );
}

export { GROUP_ORDER, WARNING_CODE_GROUPS, WARNING_CODE_LABELS };

export default CableTrayWarningPanel;
